import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Search, Loader2, BookOpen, ChevronRight, Globe } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 24;

export default function Guides() {
  const { lang, t, dir } = useLang();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [categories, setCategories] = useState([]);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => { setSkip(0); }, [debounced, category, lang]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const p = new URLSearchParams({ lang, limit: PAGE, skip });
      if (debounced) p.set("search", debounced);
      if (category) p.set("category", category);
      const { data: d } = await axios.get(`${API}/guides?${p}`);
      setData((prev) => ({ ...d, items: skip === 0 ? d.items : [...prev.items, ...d.items] }));
      if (skip === 0 && d.categories?.length) setCategories(d.categories);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [lang, skip, debounced, category]);

  useEffect(() => { load(); }, [load]);

  const countLabel = `${data.total} ${data.total === 1 ? t("common.result") : t("common.results")}`;

  return (
    <div className="es-page" data-testid="page-guides" dir={dir}>
      <Seo
        title="Evony Guides — Generals, Bosses, Research & Strategy | Evony Strategist"
        description="Discover Evony strategy guides for generals, bosses, buildings, research and more, in your language, sourced from the Evony Guide Wiki."
        path="/guides"
      />
      <header className="es-page-head">
        <h1 className="es-h1"><BookOpen size={22} className="es-h1-icon" /> {t("guides.title")}</h1>
        <p className="es-lead">{t("guides.subtitle")}</p>
      </header>

      <div className="es-search">
        <Search size={18} className="es-search-icon" />
        <input data-testid="guides-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("guides.search")} aria-label={t("guides.search")} />
      </div>

      {categories.length > 0 && (
        <div className="es-facets" data-testid="guides-categories">
          <button className={`es-facet ${!category ? "active" : ""}`} onClick={() => setCategory("")} data-testid="guides-cat-all">{t("guides.allCategories")}</button>
          {categories.map((c) => (
            <button key={c.category} className={`es-facet ${category === c.category ? "active" : ""}`}
              data-testid={`guides-cat-${c.category}`} onClick={() => setCategory(category === c.category ? "" : c.category)}>
              {c.category} <span className="es-facet-count">{c.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="es-result-count" data-testid="guides-count">{countLabel}</div>

      {error ? (
        <div className="es-state" data-testid="guides-error">{t("common.networkError")}
          <button className="es-btn-ghost" onClick={load}>{t("common.retry")}</button></div>
      ) : data.items.length === 0 && !loading ? (
        <div className="es-state" data-testid="guides-empty">{t("common.noResults")}</div>
      ) : (
        <div className="es-card-grid" data-testid="guides-grid">
          {data.items.map((g) => (
            <Link key={g.slug} to={`/guides/${g.slug}`} className="es-guide-card" data-testid={`guide-card-${g.slug}`}>
              <div className="es-guide-cat">{g.category || "Evony"}</div>
              <div className="es-guide-title">{g.title}</div>
              <div className="es-guide-foot">
                <span className="es-guide-langs"><Globe size={13} /> {g.availableLanguages?.length || 1}</span>
                <ChevronRight size={16} className="es-entity-chev" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {loading && <div className="es-state"><Loader2 className="es-spin" size={20} /> {t("common.loading")}</div>}
      {!loading && data.items.length < data.total && (
        <div className="es-loadmore-wrap">
          <button className="es-btn-secondary" data-testid="guides-loadmore" onClick={() => setSkip(skip + PAGE)}>
            {t("common.showMore")}
          </button>
        </div>
      )}
    </div>
  );
}
